import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Clock, FileText, Target } from "lucide-react";
import { format } from "date-fns";
import { useParentData } from "@/hooks/useParentData";

export default function ParentTests() {
  const { children, testInvitations, loading } = useParentData();

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-6">
        <h1 className="text-3xl font-bold">Tests</h1>
        <p className="text-muted-foreground">Tests assigned to your children by their instructors.</p>
      </header>
      <Card>
        <CardHeader>
          <CardTitle>Assigned Tests</CardTitle>
          <CardDescription>{testInvitations.filter((t) => !t.completed_at).length} pending</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : testInvitations.length > 0 ? (
            <div className="space-y-4">
              {testInvitations.map((invitation) => (
                <div key={invitation.id} className="p-4 border rounded-lg bg-card">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-medium text-lg">{invitation.instructor_tests.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        For: {children.find((c) => c.student_email === invitation.email)?.student_name || invitation.email}
                      </p>
                      {invitation.instructor_tests.description && (
                        <p className="text-xs text-muted-foreground mt-1">{invitation.instructor_tests.description}</p>
                      )}
                    </div>
                    <Badge variant={invitation.completed_at ? "secondary" : "default"}>
                      {invitation.completed_at ? "Completed" : "Pending"}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm mb-3">
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-muted-foreground" />
                      <span>{invitation.instructor_tests.subject}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-muted-foreground" />
                      <span>Grade {invitation.instructor_tests.grade}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span>{invitation.instructor_tests.total_questions} questions</span>
                    </div>
                    {invitation.instructor_tests.time_limit && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>{invitation.instructor_tests.time_limit} min</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      Sent: {format(new Date(invitation.sent_at), "MMM dd, yyyy")}
                      {invitation.completed_at && (
                        <span> • Completed: {format(new Date(invitation.completed_at), "MMM dd, yyyy")}</span>
                      )}
                    </div>
                    {invitation.score !== null && <Badge variant="outline">Score: {invitation.score}%</Badge>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No tests assigned yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
